import { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, Check, Crown, Lock, ArrowRight, Copy } from "lucide-react";
import { toast } from "sonner";

const TRIAL_KEY = "free_trial_used_v1";

type Result = {
  seo_title: string;
  seo_description: string;
  keywords: string[];
  tags: string[];
  seo_score: number;
};

export default function TryItFreeModal({
  trigger,
  open,
  onOpenChange,
}: {
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (v: boolean) => void;
}) {
  const [productName, setProductName] = useState("");
  const [category, setCategory] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  const [limitOpen, setLimitOpen] = useState(false);
  const [usedBefore, setUsedBefore] = useState<boolean>(
    typeof window !== "undefined" && !!localStorage.getItem(TRIAL_KEY),
  );

  const generate = async () => {
    if (!productName.trim()) {
      toast.error("Enter a product name first");
      return;
    }
    if (usedBefore) {
      setLimitOpen(true);
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("generate-seo-trial", {
        body: {
          product_name: productName.trim(),
          category: category.trim(),
          description: description.trim(),
        },
      });

      // supabase-js throws FunctionsHttpError on non-2xx and stuffs the Response in error.context
      let payload: any = data;
      if (error) {
        const ctx = (error as any).context;
        if (ctx && typeof ctx.json === "function") {
          try { payload = await ctx.json(); } catch { payload = null; }
        }
        if (!payload?.error) throw error;
      }

      if (payload?.error) {
        if (payload.error === "trial_used") {
          localStorage.setItem(TRIAL_KEY, "1");
          setUsedBefore(true);
          setLimitOpen(true);
          return;
        }
        throw new Error(payload.message || payload.error);
      }

      setResult(payload.result);
      localStorage.setItem(TRIAL_KEY, "1");
      setUsedBefore(true);
      toast.success("Generated! Sign up to save & generate more.");
    } catch (e: any) {
      toast.error(e?.message || "Generation failed");
    } finally {
      setLoading(false);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="h-5 w-5 text-primary" />
            Try it free - no signup
          </DialogTitle>
          <DialogDescription>
            One free generation per visitor. See what our AI can do for your listings.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4 pt-2">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Product name *</label>
              <Input
                placeholder="e.g. Insulated Stainless Steel Water Bottle"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                disabled={loading || usedBefore}
                maxLength={200}
                autoFocus
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-1.5 block">Category (optional)</label>
                <Input
                  placeholder="Drinkware, Electronics…"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  disabled={loading || usedBefore}
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block">Short description (optional)</label>
                <Input
                  placeholder="A few words about it…"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={loading || usedBefore}
                />
              </div>
            </div>
            <Button
              onClick={generate}
              disabled={loading || usedBefore || !productName.trim()}
              size="lg"
              className="w-full bg-gradient-primary shadow-glow"
            >
              {loading ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Generating…</>
              ) : usedBefore ? (
                <><Lock className="h-4 w-4 mr-2" /> Free trial used - sign up</>
              ) : (
                <><Sparkles className="h-4 w-4 mr-2" /> Generate free SEO</>
              )}
            </Button>
            {usedBefore && (
              <Button asChild variant="outline" className="w-full">
                <Link to="/auth">Sign up to keep generating</Link>
              </Button>
            )}
            <p className="text-xs text-muted-foreground text-center">
              Powered by Gemini Flash · 1 free trial per visitor
            </p>
          </div>
        )}

        {result && (
          <div className="space-y-4 pt-2">
            <div className="flex items-center justify-between">
              <Badge className="bg-gradient-primary border-0 gap-1">
                <Sparkles className="h-3 w-3" /> AI result
              </Badge>
              <span className="text-xs font-mono text-success font-semibold">
                Score: {result.seo_score}/100
              </span>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold uppercase text-muted-foreground">Title</label>
                <button onClick={() => copy(result.seo_title)} className="text-muted-foreground hover:text-foreground"><Copy className="h-3 w-3" /></button>
              </div>
              <p className="text-sm font-medium">{result.seo_title}</p>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold uppercase text-muted-foreground">Description</label>
                <button onClick={() => copy(result.seo_description)} className="text-muted-foreground hover:text-foreground"><Copy className="h-3 w-3" /></button>
              </div>
              <p className="text-sm text-muted-foreground">{result.seo_description}</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground block mb-1.5">Keywords</label>
                <div className="flex flex-wrap gap-1">
                  {result.keywords.map((k) => (
                    <span key={k} className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-xs">{k}</span>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase text-muted-foreground block mb-1.5">Tags</label>
                <div className="flex flex-wrap gap-1">
                  {result.tags.map((t) => (
                    <span key={t} className="px-1.5 py-0.5 rounded bg-accent/20 text-accent-foreground text-xs">{t}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="rounded-lg bg-gradient-primary/10 border border-primary/30 p-4 mt-4">
              <div className="flex items-center gap-2 mb-2">
                <Crown className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold">Want even better results?</span>
              </div>
              <p className="text-xs text-muted-foreground mb-3">
                Plus & Pro use <span className="font-semibold text-foreground">GPT-5</span> for sharper copy, deeper keyword research, bulk generation, and CSV export.
              </p>
              <ul className="space-y-1 text-xs text-muted-foreground mb-3">
                <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-success" /> 120–300 generations / month</li>
                <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-success" /> Powered by GPT-5 (top tier)</li>
                <li className="flex items-center gap-1.5"><Check className="h-3 w-3 text-success" /> Bulk generation & CSV export</li>
              </ul>
              <div className="flex flex-wrap gap-2">
                <Button asChild size="sm" className="bg-gradient-primary shadow-glow">
                  <Link to="/auth">Get Plus - $9/mo</Link>
                </Button>
                <Button asChild size="sm" variant="outline">
                  <Link to="/auth">Get Pro - $19/mo <ArrowRight className="h-3 w-3 ml-1" /></Link>
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>

      <Dialog open={limitOpen} onOpenChange={setLimitOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-primary" /> Free trial limit reached
            </DialogTitle>
            <DialogDescription>
              You've used your 1 free trial generation on this device. Sign up to get
              <span className="font-semibold text-foreground"> 5 free generations per day</span>.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 pt-2">
            <Button asChild size="lg" className="w-full bg-gradient-primary shadow-glow">
              <Link to="/auth">Sign up free - 5/day</Link>
            </Button>
            <Button variant="outline" onClick={() => setLimitOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
